import { initializeApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';
import { firebaseConfig } from './firebase-config.js';

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
auth.useDeviceLanguage();
setPersistence(auth, browserLocalPersistence).catch((e) =>
  console.warn('persistence setup failed', e),
);
const provider = new GoogleAuthProvider();

const listeners = new Set();
let currentUser = null;

// Process any pending redirect-flow result on page load.
getRedirectResult(auth).catch((err) => {
  console.error('getRedirectResult failed', err);
});

onAuthStateChanged(auth, (user) => {
  currentUser = user;
  for (const cb of listeners) {
    try { cb(user); } catch (err) { console.error('auth listener error', err); }
  }
});

// Errors that indicate the popup flow won't work in this browser context;
// fall back to full-page redirect.
const POPUP_FAILURE_CODES = new Set([
  'auth/popup-blocked',
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
]);

async function signIn() {
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (err) {
    console.warn('popup sign-in failed, falling back to redirect:', err?.code, err?.message);
    if (err && POPUP_FAILURE_CODES.has(err.code)) {
      await signInWithRedirect(auth, provider);
      return null; // page will navigate away; result handled by getRedirectResult on return
    }
    throw err;
  }
}

async function signOutUser() {
  await signOut(auth);
}

function onChange(cb) {
  listeners.add(cb);
  cb(currentUser);
  return () => listeners.delete(cb);
}

async function getIdToken(forceRefresh = false) {
  if (!currentUser) return null;
  return currentUser.getIdToken(forceRefresh);
}

function getUser() {
  return currentUser;
}

export default { signIn, signOut: signOutUser, onChange, getIdToken, getUser };
